import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import styles from './AffirmButton.module.scss';
import messages from './messages';
import affirmService from '../../services/affirm';
import { paymentMethods } from '../../slices/payments';
import { STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE } from '../../utils/common/constants';

const isStripeRegularCheckoutEnabled = window?.purTogglesEnabled?.(
  STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE,
);

const AffirmButton = ({ onSuccess, onFailure, isLoading }) => {
  const { formatMessage, locale } = useIntl();

  const cart = useSelector((state) => state.cart);
  const { affirmFinancingProgramme } = useSelector((state) => state.payments);
  const dispatch = useDispatch();

  const handleOnClick = useCallback(async () => {
    const affirmObject = affirmService.buildAffirmObject(cart, affirmFinancingProgramme, locale);
    await window.affirm.checkout(affirmObject);
    await window.affirm.checkout.open({
      onFail() {
        if (isStripeRegularCheckoutEnabled) {
          // Reset the payment method back to credit card.
          dispatch({
            type: 'setStatus',
            status: 'idle',
          });
          dispatch({
            type: 'setPaymentStatus',
            status: 'idle',
          });
          onFailure(paymentMethods.CREDIT_CARD);
        }
      },
      onSuccess(successObject) {
        onSuccess(successObject);
      },
    });
  }, [cart, affirmFinancingProgramme, locale, onFailure, onSuccess]);

  return (
    <div className={styles['affirm-button-wrapper']}>
      <div className={styles['affirm-button']} />
      <button
        type="button"
        onClick={handleOnClick}
        className={classnames(styles['custom-affirm-button'], 'btn', {
          [styles.disabled]: isLoading,
        })}
      >
        <div className={styles['img-container']}>
          <img
            alt={formatMessage(messages.affirmLogoAlt)}
            src={`${process.env.ASSETS_URL}/images/svg/affirm-logo.svg`}
          />
        </div>
      </button>
    </div>
  );
};

AffirmButton.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  onFailure: PropTypes.func,
  isLoading: PropTypes.bool,
};

AffirmButton.defaultProps = {
  isLoading: false,
  onFailure: () => {},
};

export default AffirmButton;
